import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { PhotoPicker } from './PhotoPicker';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: '', phone: '', email: '', address: '', details: '', services: [], sms_consent: false };

export const EstimateForm = ({ options }) => {
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const toggle = (id) => setForm((p) => ({
    ...p, services: p.services.includes(id) ? p.services.filter((s) => s !== id) : [...p.services, id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.services.length) { toast.error('Pick at least one service'); return; }
    if (uploading) { toast.error('Please wait for photos to finish uploading'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/estimates`, { ...form, email: form.email || null, photo_ids: photos.map((p) => p.id) });
      if (window.gtag) window.gtag('event', 'estimate_request_submitted', { services: form.services.join(',') });
      setDone(true);
      setForm(EMPTY);
      setPhotos([]);
    } catch (err) {
      toast.error('Could not send your request', { description: 'Please call or text us at 469-505-4321.' });
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="w-form-done" data-testid="estimate-success">
        <CheckCircle2 size={40} />
        <h3>Request received!</h3>
        <p>We'll call you within 1 business day to set up your free estimate.</p>
        <Button variant="outline" onClick={() => setDone(false)} data-testid="estimate-new-request">Send another request</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-form" data-testid="estimate-form">
      <div className="w-form-services">
        <Label>What do you need?</Label>
        <div className="w-chip-row">
          {options.map((o) => (
            <button type="button" key={o.id} onClick={() => toggle(o.id)} data-testid={`estimate-service-${o.id}`}
              className={`w-chip${form.services.includes(o.id) ? ' is-on' : ''}`} aria-pressed={form.services.includes(o.id)}>
              {o.title}
            </button>
          ))}
        </div>
      </div>
      <div className="w-form-grid">
        <div><Label htmlFor="est-name">Name</Label>
          <Input id="est-name" name="name" value={form.name} onChange={set} required data-testid="estimate-name-input" /></div>
        <div><Label htmlFor="est-phone">Phone</Label>
          <Input id="est-phone" name="phone" type="tel" value={form.phone} onChange={set} required data-testid="estimate-phone-input" /></div>
        <div><Label htmlFor="est-email">Email (optional)</Label>
          <Input id="est-email" name="email" type="email" value={form.email} onChange={set} data-testid="estimate-email-input" /></div>
        <div><Label htmlFor="est-address">Property Address</Label>
          <Input id="est-address" name="address" value={form.address} onChange={set} required data-testid="estimate-address-input" /></div>
      </div>
      <div><Label htmlFor="est-details">Tell us about the job (optional)</Label>
        <Textarea id="est-details" name="details" rows={3} value={form.details} onChange={set}
          placeholder="Broken heads, leaks, number of zones, new yard..." data-testid="estimate-details-input" /></div>
      <div><Label>Photos (optional)</Label>
        <PhotoPicker photos={photos} setPhotos={setPhotos} busy={uploading} setBusy={setUploading} /></div>
      <label className="w-consent" data-testid="estimate-sms-consent">
        <input type="checkbox" checked={form.sms_consent} data-testid="estimate-sms-checkbox"
          onChange={(e) => setForm((p) => ({ ...p, sms_consent: e.target.checked }))} />
        <span><strong>(Optional)</strong> Text me about this estimate request. Msg &amp; data rates may apply; frequency varies.
          Reply STOP to opt out, HELP for help. Not required to submit. We never send marketing texts.
          See <Link to="/privacy">Privacy</Link> &amp; <Link to="/terms">Terms</Link>.</span>
      </label>
      <Button type="submit" size="lg" className="w-btn-primary w-form-submit" disabled={sending || uploading > 0} data-testid="estimate-submit">
        {sending ? 'Sending...' : uploading ? 'Uploading photos...' : 'Request My Free Estimate'}
      </Button>
    </form>
  );
};

export default EstimateForm;
